/**
 * Confirmation / status page for a verification case.
 *
 * Fetches the case via `GET /v1/applicant/cases/:caseId` and displays
 * the appropriate status message based on the server-computed `nextStep`
 * directive. Acts as both the post-submission confirmation and a general
 * case status page.
 *
 * Status icons use contextual colours: green for approved, amber for
 * pending/in-progress, and red for rejected/error states.
 *
 * @module pages/ConfirmationPage
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { applicant } from '@noxverify/api-contracts';
import type { ApiClientError } from '@noxverify/api-client';
import type { ApplicantContext } from '../modules/context/context-loader';
import { getApiClient } from '../lib/api';
import { StepIndicator } from '../components/StepIndicator';
import { Footer } from '../components/Footer';

// ---------------------------------------------------------------------------
// Status content configuration
// ---------------------------------------------------------------------------

interface StatusContent {
  /** Title displayed below the icon. */
  title: string;
  /** Descriptive paragraph. */
  description: string;
  /** Informational banner text shown in the blue box (optional). */
  banner?: string;
  /** Step indicator position. */
  step: string;
  /** Icon variant. */
  icon: 'check' | 'clock' | 'alert';
  /** Background colour for the icon circle. */
  iconBg: string;
}

function getStatusContent(
  nextStep: applicant.CaseNextStep | null,
  status: applicant.CaseStatus | null,
  t: TFunction,
): StatusContent {
  if (!nextStep) {
    // Fallback when case data is not yet loaded.
    return {
      title: t('confirmation.status.inProgressTitle'),
      description: t('confirmation.status.inProgressDescription'),
      banner: t('confirmation.status.emailNotification'),
      step: 'complete',
      icon: 'check',
      iconBg: '#1B57D6', // primary -- default/primary
    };
  }

  switch (nextStep.action) {
    case 'wait':
      return {
        title: t('confirmation.status.inProgressTitle'),
        description: t('confirmation.status.waitDescription'),
        banner: t('confirmation.status.emailNotification'),
        step: 'complete',
        icon: 'clock',
        iconBg: '#B5751A', // warning (pending)
      };
    case 'complete':
      if (status === 'approved') {
        return {
          title: t('confirmation.status.approvedTitle'),
          description: t('confirmation.status.approvedDescription'),
          step: 'complete',
          icon: 'check',
          iconBg: '#1B57D6', // primary (approved)
        };
      }
      if (status === 'rejected') {
        return {
          title: t('confirmation.status.rejectedTitle'),
          description: t('confirmation.status.rejectedDescription'),
          step: 'complete',
          icon: 'alert',
          iconBg: '#C0392B', // danger (rejected)
        };
      }
      return {
        title: t('confirmation.status.completeTitle'),
        description: t('confirmation.status.completeDescription'),
        step: 'complete',
        icon: 'check',
        iconBg: '#1B57D6', // primary (approved)
      };
    case 'restart':
      return {
        title: t('confirmation.status.expiredTitle'),
        description: t('confirmation.status.expiredDescription'),
        step: 'start',
        icon: 'alert',
        iconBg: '#C0392B', // danger (rejected)
      };
    case 'contact_support':
      return {
        title: t('confirmation.status.suspendedTitle'),
        description: t('confirmation.status.suspendedDescription'),
        step: 'complete',
        icon: 'alert',
        iconBg: '#C0392B', // danger (rejected)
      };
    default:
      return {
        title: t('confirmation.status.inProgressTitle'),
        description: nextStep.message,
        banner: t('confirmation.status.emailNotification'),
        step: 'complete',
        icon: 'check',
        iconBg: '#B5751A', // warning (pending)
      };
  }
}

// ---------------------------------------------------------------------------
// Icon components
// ---------------------------------------------------------------------------

function CheckIcon() {
  return (
    <svg
      className="h-8 w-8 text-white"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={3}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      className="h-8 w-8 text-white"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg
      className="h-8 w-8 text-white"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v2m0 4h.01M10.29 3.86l-8.58 14.85A1 1 0 002.57 20h18.86a1 1 0 00.86-1.29L13.71 3.86a1 1 0 00-1.42 0z"
      />
    </svg>
  );
}

function StatusIcon({ icon }: { icon: StatusContent['icon'] }) {
  switch (icon) {
    case 'check':
      return <CheckIcon />;
    case 'clock':
      return <ClockIcon />;
    case 'alert':
      return <AlertIcon />;
  }
}

// ---------------------------------------------------------------------------
// Copy button for case reference
// ---------------------------------------------------------------------------

function CopyButton({ text }: { text: string }) {
  const { t } = useTranslation('portal');
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the text visually (non-critical)
    }
  }, [text]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink-muted focus:outline-none focus:ring-2 focus:ring-offset-1"
      style={{ '--tw-ring-color': 'var(--nv-primary-color)' } as React.CSSProperties}
      aria-label={copied ? t('confirmation.copiedAriaLabel') : t('confirmation.copyAriaLabel', { ref: text })}
    >
      {copied ? (
        <>
          <svg className="h-3.5 w-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-primary">{t('confirmation.copied')}</span>
        </>
      ) : (
        <>
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
            />
          </svg>
          <span>{t('confirmation.copy')}</span>
        </>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

interface ConfirmationPageProps {
  context: ApplicantContext;
}

export function ConfirmationPage({ context }: ConfirmationPageProps) {
  const { caseId } = useParams<{ caseId: string }>();
  const { t } = useTranslation('portal');

  const [caseData, setCaseData] = useState<applicant.CaseDetail | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fetching, setFetching] = useState(!!caseId);

  // Fetch case status on mount.
  useEffect(() => {
    if (!caseId) return;

    let cancelled = false;

    async function fetchCase() {
      try {
        const client = getApiClient();
        const res = await client.applicant.getCase(caseId!);
        if (!cancelled) {
          setCaseData(res.data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const apiErr = err as ApiClientError;
          if (apiErr.status === 404) {
            setFetchError(t('confirmation.caseNotFound'));
          } else {
            setFetchError(t('confirmation.loadFailed'));
          }
        }
      } finally {
        if (!cancelled) {
          setFetching(false);
        }
      }
    }

    void fetchCase();
    return () => {
      cancelled = true;
    };
  }, [caseId, t]);

  const content = getStatusContent(
    caseData?.nextStep ?? null,
    caseData?.status ?? null,
    t,
  );

  const supportEmail = context.branding.supportEmail;

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      {/* Header */}
      <header className="border-b border-hairline bg-surface">
        <div className="mx-auto flex max-w-2xl items-center gap-4 px-4 py-4">
          {context.branding.logoUrl && (
            <img
              src={context.branding.logoUrl}
              alt={t('app.logoAlt', { projectSlug: context.projectSlug })}
              className="h-8"
            />
          )}
          <h1
            className="text-lg font-semibold"
            style={{ color: 'var(--nv-primary-color)' }}
          >
            {fetching ? t('confirmation.loading') : t('app.identityVerification')}
          </h1>
        </div>
      </header>

      {/* Step indicator */}
      <div className="mx-auto w-full max-w-2xl px-4">
        <StepIndicator currentStep={content.step} />
      </div>

      <main className="flex flex-1 items-center justify-center px-4 py-12">
        {/* Loading state */}
        {fetching && (
          <div className="w-full max-w-md rounded-lg border border-hairline bg-surface-raised p-8 text-center">
            <div
              className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2"
              style={{ borderColor: 'var(--nv-primary-color)' }}
              role="status"
              aria-label={t('confirmation.loadingCaseStatusLabel')}
            />
            <p className="text-ink-muted">{t('confirmation.loadingCaseStatus')}</p>
          </div>
        )}

        {/* Error state */}
        {!fetching && fetchError && (
          <div
            className="w-full max-w-md rounded-lg border border-hairline bg-surface-raised p-8 text-center"
            role="alert"
          >
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-danger-tint">
              <svg className="h-8 w-8 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86l-8.58 14.85A1 1 0 002.57 20h18.86a1 1 0 00.86-1.29L13.71 3.86a1 1 0 00-1.42 0z" />
              </svg>
            </div>
            <h2 className="mb-2 text-xl font-bold text-ink">
              {t('confirmation.unableToLoad')}
            </h2>
            <p className="mb-6 text-sm text-ink-muted">{fetchError}</p>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex h-11 items-center justify-center rounded-lg border border-hairline-strong px-6 text-sm font-medium text-ink-muted transition-all duration-200 hover:bg-surface-sunken"
              >
                {t('confirmation.tryAgain')}
              </button>
              <a
                href="/resume"
                className="block text-sm text-ink-muted underline hover:text-ink-muted"
              >
                {t('confirmation.goToResume')}
              </a>
            </div>
          </div>
        )}

        {/* Status content */}
        {!fetching && !fetchError && (
          <div className="w-full max-w-md space-y-4">
            {/* Main status card */}
            <div className="rounded-lg border border-hairline bg-surface-raised p-8 text-center">
              {/* Status icon with contextual color */}
              <div
                className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full"
                style={{ backgroundColor: content.iconBg }}
              >
                <StatusIcon icon={content.icon} />
              </div>

              <h2 className="mb-2 font-display text-3xl font-medium tracking-tight text-ink">
                {content.title}
              </h2>
              <p className="mb-4 text-sm text-ink-muted">{content.description}</p>

              {/* Estimated processing time for pending states */}
              {content.icon === 'clock' && (
                <p className="mb-4 text-xs text-ink-muted">
                  {t('confirmation.estimatedTime')}
                </p>
              )}

              {/* Case reference with copy button */}
              {caseId && (
                <div className="mb-6 inline-flex items-center gap-2.5 rounded-md border border-hairline bg-surface-sunken px-3 py-2">
                  <span className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-muted">
                    {t('confirmation.caseRefLabel')}
                  </span>
                  <code className="font-mono text-xs text-ink">{caseId}</code>
                  <CopyButton text={caseId} />
                </div>
              )}

              {content.banner && (
                <div className="mb-4 rounded-md border border-info/30 bg-info-tint px-4 py-3 text-sm text-info">
                  {content.banner}
                </div>
              )}
            </div>

            {/* Support + links card */}
            <div className="rounded-lg border border-hairline bg-surface-raised p-6">
              <div className="space-y-3">
                {supportEmail && (
                  <div className="flex items-center gap-3 rounded-lg border border-hairline bg-surface-sunken px-4 py-3">
                    <svg
                      className="h-5 w-5 shrink-0 text-ink-muted"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                      />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-ink-muted">{t('confirmation.contactSupport')}</p>
                      <a
                        href={`mailto:${supportEmail}`}
                        className="text-sm font-medium underline hover:opacity-80"
                        style={{ color: 'var(--nv-primary-color)' }}
                      >
                        {supportEmail}
                      </a>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2 pt-2 text-center">
                  <a
                    href="/resume"
                    className="inline-flex h-11 items-center justify-center rounded-lg border border-hairline-strong px-6 text-sm font-medium text-ink-muted transition-all duration-200 hover:bg-surface-sunken"
                  >
                    {t('confirmation.useResumePage')}
                  </a>
                  <a
                    href="/"
                    className="text-sm text-ink-muted underline hover:text-ink-muted"
                  >
                    {t('confirmation.startNew')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer branding={context.branding} />
    </div>
  );
}
